<template>
  <div :class="['ui-input', sizeClass, { 'ui-input--disabled': disabled }]">
    <span
      v-if="$slots.prepend"
      class="ui-input__addon ui-input__addon--prepend"
    >
      <slot name="prepend" />
    </span>
    <input
      :id="id"
      ref="inputRef"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :name="name"
      :autocomplete="autocomplete"
      :aria-label="ariaLabel"
      :aria-invalid="invalid || undefined"
      :class="['ui-input__field', { 'ui-input__field--has-prepend': $slots.prepend, 'ui-input__field--has-append': $slots.append }]"
      v-bind="$attrs"
      @input="onInput"
      @change="onChange"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
      @keydown.enter="emit('submit', modelValue)"
    >
    <span
      v-if="$slots.append"
      class="ui-input__addon ui-input__addon--append"
    >
      <slot name="append" />
    </span>
  </div>
</template>

<script setup lang="ts">
  /**
   * UiInput — Tailwind primitive replacing BootstrapVue-Next's
   * `b-form-input`.
   *
   * Visual baseline matches the MK3 reference: gray-300 bordered, rounded
   * md, primary-600 focus ring. Supports leading/trailing addon slots
   * (icons, clear buttons, currency symbols).
   *
   * Usage:
   *   <UiInput v-model="email" type="email" placeholder="you@example.com" />
   *   <UiInput v-model="search">
   *     <template #prepend><i class="fa fa-search" /></template>
   *   </UiInput>
   *
   * Three sizes: sm / md (default) / lg.
   */
  import { ref, computed } from 'vue'

  defineOptions({ name: 'UiInput', inheritAttrs: false })

  type InputType =
    | 'text' | 'email' | 'password' | 'tel' | 'url' | 'search' | 'number' | 'range'

  /**
   * v-model modifier flags forwarded by the parent. Vue's compiler magic
   * for `.trim` / `.number` / `.lazy` only auto-applies to native inputs;
   * custom components have to read them off this prop and act on them
   * themselves. Without this support, `v-model.trim` is silently a no-op.
   */
  interface ModelModifiers {
    trim?: boolean
    number?: boolean
    lazy?: boolean
  }

  interface Props {
    modelValue?: string | number
    modelModifiers?: ModelModifiers
    type?: InputType
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    size?: 'sm' | 'md' | 'lg'
    id?: string
    name?: string
    autocomplete?: string
    ariaLabel?: string
    invalid?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    modelModifiers: () => ({}),
    type: 'text',
    placeholder: '',
    disabled: false,
    readonly: false,
    size: 'md',
    id: undefined,
    name: undefined,
    autocomplete: undefined,
    ariaLabel: undefined,
    invalid: false,
  })

  const emit = defineEmits<{
    'update:modelValue': [value: string | number]
    'change': [value: string | number]
    'focus': [event: FocusEvent]
    'blur': [event: FocusEvent]
    'submit': [value: string | number]
  }>()

  const inputRef = ref<HTMLInputElement | null>(null)

  const sizeClass = computed(() => `ui-input--${props.size}`)

  function coerce (raw: string): string | number {
    // Trim before numeric coercion — `' 42 '` should still come out as 42
    // when the parent set both `.trim` and `type="number"`.
    const trimmed = props.modelModifiers.trim ? raw.trim() : raw
    if ((props.type === 'number' || props.modelModifiers.number) && trimmed !== '') {
      const n = Number(trimmed)
      return Number.isNaN(n) ? trimmed : n
    }
    return trimmed
  }

  function onInput (event: Event) {
    // `.lazy` defers the v-model update to the change event (the parent
    // already opted in by writing `v-model.lazy`), so suppress here.
    if (props.modelModifiers.lazy) return
    const value = coerce((event.target as HTMLInputElement).value)
    emit('update:modelValue', value)
  }

  function onChange (event: Event) {
    const value = coerce((event.target as HTMLInputElement).value)
    if (props.modelModifiers.lazy) emit('update:modelValue', value)
    emit('change', value)
  }

  defineExpose({
    /** Imperative focus for cases like search-on-mount or modal openers. */
    focus: () => inputRef.value?.focus(),
    blur: () => inputRef.value?.blur(),
    /** The underlying <input> if a caller really needs it. */
    input: inputRef,
  })
</script>

<style scoped>
  /* Wrapper. `display: block` so the input fills its parent; callers
     constrain width with their own utility classes. */
  .ui-input {
    position: relative;
    display: flex;
    align-items: stretch;
    width: 100%;
  }

  .ui-input__field {
    flex: 1 1 auto;
    width: 100%;
    border: 1px solid #d1d5db;          /* gray-300 */
    border-radius: 0.375rem;
    background-color: #fff;
    color: #111827;                     /* gray-900 */
    font-size: 0.875rem;
    line-height: 1.25rem;
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }
  .ui-input__field::placeholder {
    color: #9ca3af;                     /* gray-400 */
  }
  .ui-input__field:focus {
    outline: none;
    border-color: #2563eb;              /* primary-600 */
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
  .ui-input__field:disabled,
  .ui-input__field[readonly] {
    background-color: #f9fafb;          /* gray-50 */
    color: #6b7280;                     /* gray-500 */
    cursor: not-allowed;
  }
  .ui-input__field[aria-invalid="true"] {
    border-color: #dc2626;              /* red-600 */
  }
  .ui-input__field[aria-invalid="true"]:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
  }

  /* Sizes */
  .ui-input--sm .ui-input__field {
    height: 1.875rem;                   /* 30px */
    padding: 0.25rem 0.625rem;
    font-size: 0.8125rem;
  }
  .ui-input--md .ui-input__field {
    height: 2.25rem;                    /* 36px */
    padding: 0.375rem 0.75rem;
  }
  .ui-input--lg .ui-input__field {
    height: 2.75rem;                    /* 44px */
    padding: 0.625rem 1rem;
    font-size: 1rem;
  }

  /* Addons (prepend / append). Positioned absolutely over the input so the
     input can keep `width: 100%` for v-model semantics. The field's padding
     adapts via the modifier classes below. */
  .ui-input__addon {
    position: absolute;
    top: 0;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    color: #9ca3af;                     /* gray-400 */
    pointer-events: none;
    z-index: 1;
  }
  /* When the addon hosts an interactive control (e.g. clear button) the
     caller can opt back in: <span class="ui-input__addon" style="pointer-events:auto">. */
  .ui-input__addon :deep(button),
  .ui-input__addon :deep(a) {
    pointer-events: auto;
  }
  .ui-input__addon--prepend { left: 0.625rem; }
  .ui-input__addon--append  { right: 0.5rem; }

  /* Specificity bumped to two classes so these win against the size
     rules above (`.ui-input--md .ui-input__field` would otherwise reset
     padding via the shorthand). */
  .ui-input .ui-input__field--has-prepend { padding-left: 2rem; }
  .ui-input .ui-input__field--has-append  { padding-right: 2rem; }

  .ui-input--disabled { opacity: 0.85; }
</style>
